import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen min-h-dvh bg-gradient-to-br from-slate-50 via-slate-50/95 to-teal-50/30 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate("/")}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200/60 p-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
            <p className="text-sm text-slate-500">Last Updated: December 26, 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-lg border border-slate-200/60 p-8 space-y-6">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              DsaLens ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our DSA partner application.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">2. Information We Collect</h2>
            <div className="space-y-3 text-slate-600 leading-relaxed">
              <p className="font-semibold text-slate-900">Personal Information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name, phone number, and contact details</li>
                <li>Professional information and DSA credentials</li>
                <li>Authentication data (OTP verification)</li>
                <li>Profile information and preferences</li>
              </ul>

              <p className="font-semibold text-slate-900 mt-4">Business Data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Lead and customer information you enter</li>
                <li>Transaction and loan processing data</li>
                <li>Communication logs and notes</li>
                <li>Performance metrics and analytics</li>
              </ul>

              <p className="font-semibold text-slate-900 mt-4">Technical Information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Device information and identifiers</li>
                <li>IP address and location data</li>
                <li>Browser type and operating system</li>
                <li>Usage patterns and application interactions</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed">
              We use the collected information for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-3">
              <li>Providing and maintaining the application services</li>
              <li>Processing and managing loan applications</li>
              <li>Authenticating users and securing accounts</li>
              <li>Generating reports and analytics</li>
              <li>Communicating updates and important information</li>
              <li>Improving application features and user experience</li>
              <li>Complying with legal and regulatory requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">4. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-3">
              <li>End-to-end encryption for data transmission</li>
              <li>Secure cloud storage with regular backups</li>
              <li>Multi-factor authentication (OTP-based login)</li>
              <li>Regular security audits and updates</li>
              <li>Role-based access controls</li>
              <li>Monitoring for unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">5. Data Sharing and Disclosure</h2>
            <div className="space-y-3 text-slate-600 leading-relaxed">
              <p className="font-semibold text-red-600">
                ⚠️ CONFIDENTIALITY NOTICE
              </p>
              <p>
                We do not sell or rent your personal information to third parties. We may share information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
                <li><strong>Internal Team:</strong> With authorized team members for business operations</li>
                <li><strong>Banking Partners:</strong> For loan processing and verification (with necessary permissions)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
                <li><strong>Service Providers:</strong> With trusted vendors who assist in application operations (under strict confidentiality agreements)</li>
              </ul>
              <p className="font-semibold text-slate-900 mt-4">
                You are prohibited from sharing any customer or business data from this application with unauthorized parties.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">6. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide services. We may retain certain information for longer periods to comply with legal obligations, resolve disputes, and enforce agreements.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">7. Your Rights</h2>
            <p className="text-slate-600 leading-relaxed">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-3">
              <li>Access your personal information</li>
              <li>Request corrections to inaccurate data</li>
              <li>Request deletion of your data (subject to legal requirements)</li>
              <li>Opt-out of certain data uses</li>
              <li>Lodge complaints with data protection authorities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">8. Cookies and Tracking</h2>
            <p className="text-slate-600 leading-relaxed">
              We use cookies and similar tracking technologies to maintain user sessions, remember preferences, and analyze application usage. You can control cookie settings through your browser, but disabling cookies may affect application functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">9. Third-Party Services</h2>
            <p className="text-slate-600 leading-relaxed">
              Our application may integrate with third-party services (authentication, analytics, cloud storage). These services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">10. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              DsaLens is intended for use by authorized business professionals. We do not knowingly collect information from individuals under 18 years of age.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">11. Changes to Privacy Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy periodically. We will notify you of significant changes through the application or via email. Continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">12. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact your team administrator or our data protection officer.
            </p>
          </section>

          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mt-8">
            <p className="text-sm text-teal-800 font-semibold">
              🔒 Your privacy is important to us. We are committed to protecting your information and maintaining the confidentiality of all business data in the application.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
